import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { PageHeader, Button, Spinner, Alert } from '@orientamos/ui'
import { ArrowLeft } from 'lucide-react'
import { IntakeForm } from '@features/intake'
import { getIntake, type IntakeDetail } from '@lib/api'
import type { IntakeFormData } from '@orientamos/shared'

/**
 * Transform API response to form data format
 */
function transformToFormData(intake: IntakeDetail): Partial<IntakeFormData> {
  const tenant = intake.parties.find((p) => p.role === 'tenant')
  const guarantors = intake.parties.filter((p) => p.role === 'guarantor')
  const owners = intake.parties.filter((p) => p.role === 'owner')

  const transformParty = (party: IntakeDetail['parties'][0]) => {
    const base = {
      personType: party.personType as 'natural' | 'legal',
      firstName: party.firstName,
      lastName: party.lastName,
      idNumber: party.idNumber,
      idIssuedAt: party.idIssuedAt || '',
      profession: party.profession || '',
      phone: party.phone,
      address: party.address,
      city: party.city,
      email: party.email,
      invoiceEmail: party.invoiceEmail || '',
      role: party.role as 'tenant' | 'guarantor' | 'owner',
      sortOrder: party.sortOrder,
      dataAuthorizationAccepted: party.dataAuthorizationAccepted,
      referenceCheckAuthorized: party.referenceCheckAuthorized,
      banking: party.banking || null,
      documents: party.documents || null,
    }

    if (party.personType === 'legal') {
      return {
        ...base,
        companyName: party.companyName || '',
        nit: party.nit || '',
        legalRepName: party.legalRepName || '',
        legalRepId: party.legalRepId || '',
      }
    }

    return base
  }

  return {
    contractType: intake.contractType as 'residential' | 'commercial',
    guaranteedRent: intake.guaranteedRent,
    property: intake.property as IntakeFormData['property'],
    tenant: tenant ? transformParty(tenant) : undefined,
    guarantors: guarantors.map(transformParty),
    owners: owners.map(transformParty),
    terms: {
      ...intake.terms,
      adminFeePaidBy: 'tenant',
      gracePeriodDays: 5,
    },
    insurance: intake.insurance,
    commission: intake.commission,
    capturedBy: intake.capturedBy,
    rentedBy: intake.rentedBy,
  } as Partial<IntakeFormData>
}

export default function IntakeEditPage() {
  const { id } = useParams<{ id: string }>()

  const [initialData, setInitialData] = useState<Partial<IntakeFormData> | null>(null)
  const [orderNumber, setOrderNumber] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadIntake() {
      if (!id) return

      setIsLoading(true)
      setError(null)

      try {
        const result = await getIntake(id)
        if (result.success && result.data) {
          setInitialData(transformToFormData(result.data))
          setOrderNumber(result.data.orderNumber)
        } else {
          setError(result.error || 'Orden no encontrada')
        }
      } catch {
        setError('Error de conexion')
      } finally {
        setIsLoading(false)
      }
    }

    loadIntake()
  }, [id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !initialData) {
    return (
      <div>
        <PageHeader
          title="Orden no encontrada"
          breadcrumbs={[
            { label: 'Inicio', href: '/' },
            { label: 'Ordenes de Contrato', href: '/intakes' },
            { label: 'Error' },
          ]}
        />
        <Alert variant="danger">{error || 'La orden solicitada no existe'}</Alert>
        <div className="mt-4">
          <Link to="/intakes">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver a la lista
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title={`Editar Orden ${orderNumber}`}
        description="Modifica la informacion de la orden de contrato"
        breadcrumbs={[
          { label: 'Inicio', href: '/' },
          { label: 'Ordenes de Contrato', href: '/intakes' },
          { label: orderNumber, href: `/intakes/${id}` },
          { label: 'Editar' },
        ]}
      />

      <IntakeForm initialData={initialData} intakeId={id} />
    </div>
  )
}
